import { API_CONFIG } from '../config';
import type {
  ApiResponse,
  PaginatedResponse,
  Auction,
  NFT,
  Collection,
  User,
  Bid,
  Stake,
  Proposal,
  Vote,
  FractionalToken,
  Activity,
} from '../types';

class ApiService {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Auth
  async authenticate(walletAddress: string, message: string, signature: string): Promise<ApiResponse<{ token: string; user: User }>> {
    return this.request('/users/auth', {
      method: 'POST',
      body: JSON.stringify({ walletAddress, message, signature }),
    });
  }

  // Users
  async getUser(address: string): Promise<ApiResponse<User>> {
    return this.request(`/users/${address}`);
  }

  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    return this.request('/users/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getLeaderboard(params: {
    timeRange?: string;
    category?: string;
    limit?: number;
  }): Promise<ApiResponse<User[]>> {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request(`/users/leaderboard?${query}`);
  }

  // Auctions
  async getAuctions(params: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse<PaginatedResponse<Auction>>> {
    const query = new URLSearchParams(
      Object.entries(params).filter(([_, v]) => v !== undefined) as [string, string][]
    ).toString();
    return this.request(`/auctions?${query}`);
  }

  async getAuction(id: string): Promise<ApiResponse<Auction>> {
    return this.request(`/auctions/${id}`);
  }

  async createAuction(data: {
    nftId: string;
    type: string;
    startPrice: number;
    reservePrice?: number;
    endPrice?: number;
    startTime: string;
    endTime: string;
    minIncrement?: number;
    isPrivate?: boolean;
    requiredTier?: string;
  }): Promise<ApiResponse<Auction>> {
    return this.request('/auctions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async placeBid(auctionId: string, amount: number): Promise<ApiResponse<Bid>> {
    return this.request(`/auctions/${auctionId}/bid`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  async commitBid(auctionId: string, commitHash: string, deposit: number): Promise<ApiResponse<Bid>> {
    return this.request(`/auctions/${auctionId}/commit`, {
      method: 'POST',
      body: JSON.stringify({ commitHash, deposit }),
    });
  }

  async revealBid(auctionId: string, amount: number, secret: string): Promise<ApiResponse<Bid>> {
    return this.request(`/auctions/${auctionId}/reveal`, {
      method: 'POST',
      body: JSON.stringify({ amount, secret }),
    });
  }

  async getMyBids(params: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<Bid>>> {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request(`/auctions/my-bids?${query}`);
  }

  // Collections
  async getCollections(params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    verified?: boolean;
  }): Promise<ApiResponse<PaginatedResponse<Collection>>> {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request(`/nfts/collections?${query}`);
  }

  async getCollection(id: string): Promise<ApiResponse<Collection>> {
    return this.request(`/nfts/collections/${id}`);
  }

  // NFTs
  async getNFTs(params: {
    collectionId?: string;
    ownerId?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<NFT>>> {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request(`/nfts?${query}`);
  }

  async getNFT(id: string): Promise<ApiResponse<NFT>> {
    return this.request(`/nfts/${id}`);
  }

  async getMyNFTs(): Promise<ApiResponse<NFT[]>> {
    return this.request('/nfts/owned');
  }

  async importNFT(data: {
    contractAddress: string;
    tokenId: string;
    name: string;
    description?: string;
    image: string;
    collectionName?: string;
    collectionSymbol?: string;
  }): Promise<ApiResponse<NFT>> {
    return this.request('/nfts/import', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Staking
  async getStakingPools(): Promise<ApiResponse<Stake[]>> {
    return this.request('/staking/pools');
  }

  async stake(poolId: string, amount: number): Promise<ApiResponse<Stake>> {
    return this.request('/staking/stake', {
      method: 'POST',
      body: JSON.stringify({ poolId, amount }),
    });
  }

  async unstake(stakeId: string): Promise<ApiResponse<Stake>> {
    return this.request(`/staking/${stakeId}/unstake`, {
      method: 'POST',
    });
  }

  async claimRewards(stakeId: string): Promise<ApiResponse<{ amount: number }>> {
    return this.request(`/staking/${stakeId}/claim`, {
      method: 'POST',
    });
  }

  async getMyStakes(): Promise<ApiResponse<Stake[]>> {
    return this.request('/staking/my-stakes');
  }

  // Governance
  async getProposals(params: {
    status?: string;
    category?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<Proposal>>> {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request(`/governance/proposals?${query}`);
  }

  async getProposal(id: string): Promise<ApiResponse<Proposal>> {
    return this.request(`/governance/proposals/${id}`);
  }

  async createProposal(data: {
    title: string;
    description: string;
    category: string;
    actions?: any[];
  }): Promise<ApiResponse<Proposal>> {
    return this.request('/governance/proposals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async vote(proposalId: string, choice: 'for' | 'against'): Promise<ApiResponse<Vote>> {
    return this.request(`/governance/proposals/${proposalId}/vote`, {
      method: 'POST',
      body: JSON.stringify({ choice }),
    });
  }

  async getVotes(proposalId: string): Promise<ApiResponse<Vote[]>> {
    return this.request(`/governance/proposals/${proposalId}/votes`);
  }

  // Fractional
  async getFractionalTokens(params: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<FractionalToken>>> {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request(`/fractional?${query}`);
  }

  async getFractionalToken(id: string): Promise<ApiResponse<FractionalToken>> {
    return this.request(`/fractional/${id}`);
  }

  async buyFractions(tokenId: string, amount: number): Promise<ApiResponse<any>> {
    return this.request(`/fractional/${tokenId}/buy`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  async initiateBuyout(tokenId: string, price: number): Promise<ApiResponse<any>> {
    return this.request(`/fractional/${tokenId}/buyout`, {
      method: 'POST',
      body: JSON.stringify({ price }),
    });
  }

  // Activity
  async getActivity(params: {
    userId?: string;
    type?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<PaginatedResponse<Activity>>> {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request(`/activity?${query}`);
  }

  // Stats
  async getPlatformStats(): Promise<ApiResponse<{
    totalVolume: number;
    totalAuctions: number;
    activeAuctions: number;
    totalUsers: number;
    totalStaked: number;
  }>> {
    return this.request('/stats');
  }

  async getFloorPrice(collectionId: string): Promise<ApiResponse<{
    floorPrice: number;
    oraclePrice: number;
    mlPrice: number;
    consensusPrice: number;
  }>> {
    return this.request(`/stats/floor-price/${collectionId}`);
  }
}

export const api = new ApiService();
export default api;
